import type { OptimiseUserDetails } from '../types/optimise';
import type { RecommendedTariff, TariffComparisonResult } from '../types/tariffs';

/**
 * Compares userDetails tariff with recommendedTariffs tariff data
 * @param userDetails - User details object containing tariffs
 * @param recommendedTariffs - Array of recommended tariff objects
 * @returns Object containing comparison result and appropriate tariff data
 *
 * The main Point of this is to determine whether to show the tariff listed in the user details
 * or the one listed in the recommended tariffs. After a user updates their tariff, we want to show
 * the tariff from the user details and not the one from the recommended tariffs.
 * We do that by comparing the names of both tariffs, sourced from the user details and the recommended tariffs,
 * and if they do not match, we can see that the user has updated their tariff.
 */

// Helper function to normalise supplier names for comparison
const normaliseSupplierName = (supplier: string): string => {
  return supplier.toLowerCase().replace(/[\s_-]/g, '');
};

export const compareAndGetCurrentTariff = (
  userDetails: OptimiseUserDetails | null,
  recommendedTariffs: RecommendedTariff[] | null
): TariffComparisonResult => {
  if (!userDetails?.tariffs?.import_tariff || !userDetails?.tariffs?.export_tariff || !recommendedTariffs) {
    return {
      currentTariffData: null,
      hasTariffBeenRecentlyUpdated: false,
    };
  }

  // Find the current tariff from recommendedTariffs
  const recommendedCurrentTariff: RecommendedTariff | null =
    recommendedTariffs.find(
      (tariff: RecommendedTariff) => tariff.tariff_combination.tariff_standard_combination_id === 'current_set_tariff'
    ) || null;

  // Compare tariffs
  const userImportTariff = userDetails.tariffs.import_tariff;
  const userExportTariff = userDetails.tariffs.export_tariff;

  let tariffsMatch = false;

  if (recommendedCurrentTariff) {
    const recommendedImportTariff = recommendedCurrentTariff.tariff_combination;
    const recommendedExportTariff = recommendedCurrentTariff.tariff_combination;

    // Compare import tariffs (case-insensitive and normalised)
    const importTariffsMatch =
      normaliseSupplierName(userImportTariff.supplier) ===
        normaliseSupplierName(recommendedImportTariff.import_tariff_supplier_name) &&
      userImportTariff.tariff_name.toLowerCase() === recommendedImportTariff.import_tariff_name.toLowerCase();

    // Compare export tariffs (case-insensitive and normalised)
    const exportTariffsMatch =
      normaliseSupplierName(userExportTariff.supplier) ===
        normaliseSupplierName(recommendedExportTariff.export_tariff_supplier_name) &&
      userExportTariff.tariff_name.toLowerCase() === recommendedExportTariff.export_tariff_name.toLowerCase();

    tariffsMatch = importTariffsMatch && exportTariffsMatch;
  }

  if (tariffsMatch && recommendedCurrentTariff) {
    // Tariffs match - use recommended tariff data
    return {
      currentTariffData: recommendedCurrentTariff,
      hasTariffBeenRecentlyUpdated: false,
    };
  } else {
    // Tariffs don't match - use user details tariff
    const currentTariff: RecommendedTariff = {
      tariff_combination: {
        tariff_standard_combination_id: 'current_set_tariff',
        import_tariff_supplier_name: userImportTariff.supplier,
        import_tariff_name: userImportTariff.tariff_name,
        import_tariff_id: userImportTariff.tariff_id || '',
        export_tariff_supplier_name: userExportTariff.supplier,
        export_tariff_name: userExportTariff.tariff_name,
        export_tariff_id: userExportTariff.tariff_id || '',
      },
      // Add optimised_tariff_results with default values since we don't have simulation data
      optimised_tariff_results: {
        export_cost: '0',
        import_cost: '0',
      },
    };

    return {
      currentTariffData: currentTariff,
      hasTariffBeenRecentlyUpdated: true,
    };
  }
};
